import { useAuth } from '@/contexts/AuthContext';
import { useTenancy } from '@/contexts/TenancyContext';
import { useEffect, useState } from 'react';

/**
 * Hook to check if the current user has access to tours
 * 
 * TOUR ACCESS RULES:
 * - Users with 'academy' role have access to all tours
 * - Users with 'owner' role (Company Admin) do NOT have tour access
 * - Users with 'user' role (Policy Reader) do NOT have tour access
 * - Users without any role do NOT have tour access
 * 
 * Future extensions:
 * - Can add role-specific tours (e.g., academy users get basic tours, owners get advanced tours)
 * - Can add feature-based tour access (e.g., only show SOA tour if SOC 2 framework is enabled)
 * - Can add permission-based tours (e.g., only show admin tours to users with admin permissions)
 */
export function useTourAccess() {
  const { user } = useAuth();
  const { users, activeTenancy } = useTenancy();
  const [hasTourAccess, setHasTourAccess] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // console.log('[DEBUG] useTourAccess - user:', user?.uid);
    // console.log('[DEBUG] useTourAccess - activeTenancy:', activeTenancy?.id);
    // console.log('[DEBUG] useTourAccess - users length:', users.length);
    
    if (!user || !activeTenancy?.id || !users.length) {
      // console.log('[DEBUG] useTourAccess - missing data, setting loading false');
      setLoading(false);
      return;
    }

    // Find the current user in the users list
    const currentUser = users.find(u => u.id === user.uid);
    // console.log('[DEBUG] useTourAccess - currentUser:', currentUser);
    
    if (currentUser) {
      // Check if user has academy role ONLY
      const hasAcademyRole = currentUser.roles?.academy === true;
      // console.log('[DEBUG] useTourAccess - hasAcademyRole:', hasAcademyRole);
      // console.log('[DEBUG] useTourAccess - user roles:', currentUser.roles);
      
      setHasTourAccess(hasAcademyRole);
      // console.log('[DEBUG] useTourAccess - setHasTourAccess called with:', hasAcademyRole);
    } else {
      // console.log('[DEBUG] useTourAccess - current user not found in users list');
      setHasTourAccess(false);
    }
    
    setLoading(false);
  }, [user, users, activeTenancy]);

  return { hasTourAccess, loading };
}
